import { Suspense, useCallback, useSyncExternalStore } from "react";
import AppErrorBoundary from "@/shared/ui/AppErrorBoundary";
import LazyLoadErrorToast from "@/shared/ui/LazyLoadErrorToast";
import { lazyWithReload } from "@/shared/utils/lazyWithReload";
import type { CorrectionsTarget } from "@/shared/types/corrections";

// The dialog drags in @mui/x-date-pickers and dayjs, so it stays out of the
// entry chunk until someone actually opens corrections.
const MakeCorrectionsDialog = lazyWithReload(
  "MakeCorrectionsDialog",
  () => import("@/shared/ui/corrections/MakeCorrectionsDialog"),
);

interface MakeCorrectionsDialogSnapshot {
  open: boolean;
  // The target is kept after close so the dialog contents stay rendered while
  // the MUI close transition plays out.
  target: CorrectionsTarget | null;
  // Bumped on every open; part of the form's React key so reopening the same
  // row starts from a fresh form instead of resurrecting stale edits.
  session: number;
}

// Module-level state — same pattern as AddToWatchlistDialogProvider.
// The dialog is a singleton; only one can be open at a time.
// React Compiler does not treat these bindings as reactive, so the mount reads
// `_snapshot` through useSyncExternalStore. A useReducer tick left `open` stuck.
let _open = false;
let _target: CorrectionsTarget | null = null;
let _session = 0;
let _snapshot: MakeCorrectionsDialogSnapshot = { open: false, target: null, session: 0 };
const _listeners = new Set<() => void>();

function publish(): void {
  _snapshot = { open: _open, target: _target, session: _session };
  for (const listener of _listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

function getSnapshot(): MakeCorrectionsDialogSnapshot {
  return _snapshot;
}

/**
 * Open the Make Corrections modal for a quote or RFQ row. Callable from any
 * feature (markets grid, bond-detail drawer, future locations) — the dialog
 * itself is mounted once inside AppProviders.
 */
export function openMakeCorrectionsDialog(target: CorrectionsTarget): void {
  _open = true;
  _target = target;
  _session += 1;
  publish();
}

/**
 * Renders the Make Corrections dialog and subscribes to module-level state.
 * Place this once inside AppProviders as a sibling to {children} — not a
 * wrapper. Callers open the dialog via `openMakeCorrectionsDialog(...)` directly.
 */
export default function MakeCorrectionsDialogMount() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const handleClose = useCallback(() => {
    _open = false;
    publish();
  }, []);

  // Never opened in this session: leave the dialog chunk unrequested.
  if (!snapshot.target) {
    return null;
  }

  // This mount is a sibling of the whole app, so an uncaught failure here would
  // blank the page rather than just the dialog.
  return (
    <AppErrorBoundary
      fallback={
        <LazyLoadErrorToast message="Could not open Make Corrections. Reload the page to try again." />
      }
    >
      <Suspense fallback={null}>
        <MakeCorrectionsDialog
          open={snapshot.open}
          target={snapshot.target}
          sessionKey={snapshot.session}
          onClose={handleClose}
        />
      </Suspense>
    </AppErrorBoundary>
  );
}
